import { Box, Center, Flex, Spinner } from "@chakra-ui/react";
import { EmptyMessage } from "@components/EmptyMessage";
import { OperationTileMultichain } from "@components/OperationTile";
import { useQuery } from "@tanstack/react-query";
import { POLLING_INTERVAL } from "@umami/chains";
import {
  type ShieldedTransaction,
  useCurrentAccount,
  useGetCurrentSaplingTransactions,
  useSelectedNetwork,
} from "@umami/state";

import { normalizeShieldedTransaction } from "./utils";

export const ShieldedActivityTab = () => {
  const getTransactions = useGetCurrentSaplingTransactions();
  const { explorerUrl } = useSelectedNetwork();
  const currentAccount = useCurrentAccount();
  const selectedNetwork = useSelectedNetwork();

  const {
    data: transactions = [],
    isLoading,
    isFetching,
  } = useQuery<ShieldedTransaction[]>({
    queryKey: [
      "saplingTransactions",
      getTransactions,
      currentAccount?.address.pkh,
      selectedNetwork.name,
    ],
    queryFn: getTransactions,
    retry: false,
    refetchOnMount: "always",
    refetchInterval: POLLING_INTERVAL, // 1 minute
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return (
      <Flex zIndex={1} flexDirection="column" flexGrow={1}>
        <Center display="flex" height="100%" minHeight="136px">
          <Spinner />
        </Center>
      </Flex>
    );
  }

  if (transactions.length === 0) {
    return (
      <Flex
        zIndex={1}
        flexDirection="column"
        flexGrow={1}
        padding={{
          md: "10px",
          base: "8px",
        }}
      >
        <Box borderRadius="8px">
          <Center display="flex" height="100%" minHeight="136px">
            <EmptyMessage
              margin="auto"
              subtitle="Shielded transaction history will appear here"
              title="No shielded transactions"
            />
          </Center>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      zIndex={1}
      flexDirection="column"
      flexGrow={1}
      padding={{
        md: "10px",
        base: "8px",
      }}
    >
      {isFetching && (
        <Center gap="10px" display="flex" paddingBottom="20px">
          <Spinner /> Fetching new Transactions...
        </Center>
      )}

      <Box borderRadius="8px">
        {transactions.map((transaction: ShieldedTransaction, index: number) => {
          const normalizedOperation = normalizeShieldedTransaction(transaction, index, explorerUrl);

          return (
            <OperationTileMultichain
              key={`sapling-${transaction.position}-${index}`}
              operation={normalizedOperation}
            />
          );
        })}
      </Box>
    </Flex>
  );
};
